import React, { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';

import { currentUserIDState } from '@atom/setting/user';
import { CollectionPath } from '@typings/collections.enum';
import { User } from '@typings/setting/userType.interface';
import Button from '@components/dataEntry/button/Button';

type Props = {
  size?: 'sm' | 'md' | 'lg';
  /**
   * 조회 기준이 되는 유저 UID.
   * [내 계정] > [캐릭터] 탭과 동일한 데이터 소스를 사용하기 위해 전달합니다.
   * 전달하지 않으면 로그인한 유저(currentUserIDState)를 기준으로 동작합니다.
   */
  userUID?: string;
};

/**
 * 빠른 대표 캐릭터 등록 버튼
 *
 * 우선순위: 로그인한 유저의 보유 캐릭터(포토카드) 1번째 → 제출한 폼 1번째
 * 둘 다 없으면 '설정 가능한 캐릭터가 없습니다' 알럿을 표시합니다.
 */
function QuickRegisterRepresentativeBtn({ size = 'md', userUID }: Props) {
  const currentUserID = useRecoilValue(currentUserIDState);
  // [내 계정] > [캐릭터] 탭과 동일한 기준: userUID prop 우선, 없으면 로그인 유저 UID
  const uid = userUID || currentUserID || '';

  // 저장 대상은 항상 로그인한 유저 본인의 문서
  const saveUID = currentUserID || '';
  const { set: setUserData } = useDocument<User>(
    `${CollectionPath.USERS}/${saveUID}`,
    { listen: false },
  );

  // 포토카드 목록
  const { data: photocards1, loading: loadingPC1 } = useCollection<any>(
    CollectionPath.PHOTOCARD,
    { listen: false, where: ['owner', '==', uid] },
  );
  const { data: photocards2, loading: loadingPC2 } = useCollection<any>(
    `${CollectionPath.PHOTOCARD}2`,
    { listen: false, where: ['owner', '==', uid] },
  );
  const { data: photocards3, loading: loadingPC3 } = useCollection<any>(
    `${CollectionPath.PHOTOCARD}3`,
    { listen: false, where: ['owner', '==', uid] },
  );

  // 제출한 폼 목록
  const { data: forms1, loading: loadingF1 } = useCollection<any>(
    CollectionPath.FORM,
    { listen: false, where: ['authorUid', '==', uid] },
  );
  const { data: forms2, loading: loadingF2 } = useCollection<any>(
    `${CollectionPath.FORM}2`,
    { listen: false, where: ['authorUid', '==', uid] },
  );
  const { data: forms3, loading: loadingF3 } = useCollection<any>(
    `${CollectionPath.FORM}3`,
    { listen: false, where: ['authorUid', '==', uid] },
  );

  const isDataLoading =
    loadingPC1 || loadingPC2 || loadingPC3 || loadingF1 || loadingF2 || loadingF3;

  const mergedPhotocards = useMemo(
    () => [
      ...(photocards1 || []).map((p: any) => ({
        ...p,
        collectionPath: CollectionPath.PHOTOCARD,
      })),
      ...(photocards2 || []).map((p: any) => ({
        ...p,
        collectionPath: `${CollectionPath.PHOTOCARD}2`,
      })),
      ...(photocards3 || []).map((p: any) => ({
        ...p,
        collectionPath: `${CollectionPath.PHOTOCARD}3`,
      })),
    ],
    [photocards1, photocards2, photocards3],
  );

  const mergedForms = useMemo(
    () => [
      ...(forms1 || []).map((f: any) => ({
        ...f,
        collectionPath: CollectionPath.FORM,
      })),
      ...(forms2 || []).map((f: any) => ({
        ...f,
        collectionPath: `${CollectionPath.FORM}2`,
      })),
      ...(forms3 || []).map((f: any) => ({
        ...f,
        collectionPath: `${CollectionPath.FORM}3`,
      })),
    ],
    [forms1, forms2, forms3],
  );

  const [pending, setPending] = useState(false);

  const handleQuickRegister = useCallback(async () => {
    if (pending || !currentUserID) return;

    // 데이터 로딩 중이면 대기
    if (isDataLoading) return;

    // 로그인한 유저의 보유 캐릭터(포토카드) 우선, 없으면 제출한 폼, 둘 다 없으면 알럿
    if (mergedPhotocards.length === 0 && mergedForms.length === 0) {
      alert('설정 가능한 캐릭터가 없습니다');
      return;
    }

    setPending(true);
    try {
      if (mergedPhotocards.length > 0) {
        const first = mergedPhotocards[0];
        await setUserData(
          {
            representativePhotocardId: first.id,
            representativePhotocardPath: first.collectionPath,
          },
          { merge: true },
        );
        return;
      }
      if (mergedForms.length > 0) {
        const first = mergedForms[0];
        await setUserData(
          {
            representativeFormId: first.id,
            representativeFormPath: first.collectionPath,
          },
          { merge: true },
        );
      }
    } finally {
      setPending(false);
    }
  }, [pending, currentUserID, isDataLoading, mergedPhotocards, mergedForms, setUserData]);

  if (!currentUserID) return null;

  return (
    <Button
      size={size}
      onClick={handleQuickRegister}
      pending={pending || isDataLoading}
    >
      빠른 대표 캐릭터 등록
    </Button>
  );
}

export default QuickRegisterRepresentativeBtn;
